// auto-generated by Typewriter template _models.tst from PortCall.EndPoints\Api\Berth\ApiBerth.cs
import S, { DataSignal } from 's-js';
import SArray, { SDataArray } from 's-array';
import TZDate from 'lib/TZDate';
import { jsonableObservable } from 'lib/jsonableObservable';
import { BerthAmenity, BerthAmenityData, BerthAmenityOptions } from 'data/models/BerthAmenity';
import { BerthCapabilityAssignment, BerthCapabilityAssignmentData, BerthCapabilityAssignmentOptions } from 'data/models/BerthCapabilityAssignment';
import { BerthClosure, BerthClosureData, BerthClosureOptions } from 'data/models/BerthClosure';
import { BerthRelevance, BerthRelevanceData, BerthRelevanceOptions } from 'data/models/BerthRelevance';
import { BollardPosition, BollardPositionData, BollardPositionOptions } from 'data/models/BollardPosition';
import { Issue, IssueData, IssueOptions } from 'data/models/Issue';
import { Location, LocationData, LocationOptions } from 'data/models/Location';
import { Measurement, MeasurementData, MeasurementOptions } from 'data/models/Measurement';
import { Organization, OrganizationData, OrganizationOptions } from 'data/models/Organization';
import { PierGroup, PierGroupData, PierGroupOptions } from 'data/models/PierGroup';
import { PostalAddress, PostalAddressData, PostalAddressOptions } from 'data/models/PostalAddress';
import { TopicCard, TopicCardData, TopicCardOptions } from 'data/models/TopicCard';
import { BerthType } from 'server/enums/BerthType';
import { CompassPoint } from 'server/enums/CompassPoint';

// BerthData is the class that Berth serializes to/from
export interface BerthData {
    Id : number;
    Url : string | null;
	
    Amenities : BerthAmenityData[] | null;
    AmenitiesUrl : string | null;
    BerthCapabilityAssignmentsUrl : string | null;
    BollardPositions : BollardPositionData[] | null;
    Capabilities : BerthCapabilityAssignmentData[] | null;
    Closures : BerthClosureData[] | null;
    Issues : IssueData[] | null;
    Location : LocationData | null;
    PierGroup : PierGroupData | null;
    PierGroupId : number;
    PostalAddress : PostalAddressData | null;
    PostalAddressId : number;
    SchedulingAuthority : OrganizationData | null;
    SchedulingAuthorityId : number | null;
    Stream : BerthRelevanceData[] | null;
    TopicCards : TopicCardData[] | null;
	
    AutoCalculateBills : boolean;
    BerthTransitOffset : number;
    BerthType : BerthType;
    DaylightMooringOnly : boolean;
    Guid : /* GUID */ string;
    Length : MeasurementData;
    LengthBuffer : MeasurementData;
    LowFootmarkCompassPoint : CompassPoint;
    MaxDraftAllowed : MeasurementData;
    MultiVesselBerthing : boolean;
    Notes : string | null;
    PierMark : MeasurementData;
    PortName : string | null;
    PortSideVesselHasBowToLowFootmark : boolean;
    StartMark : MeasurementData;
    TimeBuffer : number;
}

// BerthOptions is the class which may be passed to the Berth constructor to define optional initial data values
export interface BerthOptions {
    Id : number;
    Url? : string | null;
	
    Amenities? : BerthAmenityOptions[] | null;
    AmenitiesUrl? : string | null;
    BerthCapabilityAssignmentsUrl? : string | null;
    BollardPositions? : BollardPositionOptions[] | null;
    Capabilities? : BerthCapabilityAssignmentOptions[] | null;
    Closures? : BerthClosureOptions[] | null;
    Issues? : IssueOptions[] | null;
    Location? : LocationOptions | null;
    PierGroup? : PierGroupOptions | null;
    PierGroupId? : number;
    PostalAddress? : PostalAddressOptions | null;
    PostalAddressId? : number;
    SchedulingAuthority? : OrganizationOptions | null;
    SchedulingAuthorityId? : number | null;
    Stream? : BerthRelevanceOptions[] | null;
    TopicCards? : TopicCardOptions[] | null;
	
    AutoCalculateBills? : boolean;
    BerthTransitOffset? : number;
    BerthType? : BerthType;
    DaylightMooringOnly? : boolean;
    Guid? : /* GUID */ string;
    Length? : MeasurementOptions;
    LengthBuffer? : MeasurementOptions;
    LowFootmarkCompassPoint? : CompassPoint;
    MaxDraftAllowed? : MeasurementOptions;
    MultiVesselBerthing? : boolean;
    Notes? : string | null;
    PierMark? : MeasurementOptions;
    PortName? : string | null;
    PortSideVesselHasBowToLowFootmark? : boolean;
    StartMark? : MeasurementOptions;
    TimeBuffer? : number;
}

export class Berth {
    public Id : DataSignal<number>;
    public Url : DataSignal<string | null>;
	
    public Amenities : SDataArray<BerthAmenity>;
    public AmenitiesUrl : DataSignal<string | null>;
    public BerthCapabilityAssignmentsUrl : DataSignal<string | null>;
    public BollardPositions : SDataArray<BollardPosition>;
    public Capabilities : SDataArray<BerthCapabilityAssignment>;
    public Closures : SDataArray<BerthClosure>;
    public Issues : SDataArray<Issue>;
    public Location : DataSignal<Location | null>;
    public PierGroup : DataSignal<PierGroup | null>;
    public PierGroupId : DataSignal<number>;
    public PostalAddress : DataSignal<PostalAddress | null>;
    public PostalAddressId : DataSignal<number>;
    public SchedulingAuthority : DataSignal<Organization | null>;
    public SchedulingAuthorityId : DataSignal<number | null>;
    public Stream : SDataArray<BerthRelevance>;
    public TopicCards : SDataArray<TopicCard>;
	
    public AutoCalculateBills : DataSignal<boolean>;
    public BerthTransitOffset : DataSignal<number>;
    public BerthType : DataSignal<BerthType>;
    public DaylightMooringOnly : DataSignal<boolean>;
    public Guid : DataSignal<string>;
    public Length : Measurement;
    public LengthBuffer : Measurement;
    public LowFootmarkCompassPoint : DataSignal<CompassPoint>;
    public MaxDraftAllowed : Measurement;
    public MultiVesselBerthing : DataSignal<boolean>;
    public Notes : DataSignal<string | null>;
    public PierMark : Measurement;
    public PortName : DataSignal<string | null>;
    public PortSideVesselHasBowToLowFootmark : DataSignal<boolean>;
    public StartMark : Measurement;
    public TimeBuffer : DataSignal<number>;

    constructor();
    constructor(data : BerthOptions);
    constructor(data: BerthOptions | null = null) {
        this.Id = jsonableObservable(S.value<number>(data && data.Id !== undefined ? data.Id : 0));
        this.Url = jsonableObservable(S.value<string | null>(data && data.Url !== undefined ? data.Url : null));
	    
        this.Amenities = SArray<BerthAmenity>([]);
        this.AmenitiesUrl = jsonableObservable(S.value<string | null>(data && data.AmenitiesUrl !== undefined ? data.AmenitiesUrl : null));
        this.BerthCapabilityAssignmentsUrl = jsonableObservable(S.value<string | null>(data && data.BerthCapabilityAssignmentsUrl !== undefined ? data.BerthCapabilityAssignmentsUrl : null));
        this.BollardPositions = SArray<BollardPosition>([]);
        this.Capabilities = SArray<BerthCapabilityAssignment>([]);
        this.Closures = SArray<BerthClosure>([]);
        this.Issues = SArray<Issue>([]);
        this.Location = S.value<Location | null>(null);
        this.PierGroup = S.value<PierGroup | null>(null);
        this.PierGroupId = jsonableObservable(S.value<number>(data && data.PierGroupId !== undefined ? data.PierGroupId : 0));
        this.PostalAddress = S.value<PostalAddress | null>(null);
        this.PostalAddressId = jsonableObservable(S.value<number>(data && data.PostalAddressId !== undefined ? data.PostalAddressId : 0));
        this.SchedulingAuthority = S.value<Organization | null>(null);
        this.SchedulingAuthorityId = jsonableObservable(S.value<number | null>(data && data.SchedulingAuthorityId !== undefined ? data.SchedulingAuthorityId : null));
        this.Stream = SArray<BerthRelevance>([]);
        this.TopicCards = SArray<TopicCard>([]);
	    
        this.AutoCalculateBills = jsonableObservable(S.value<boolean>(data && data.AutoCalculateBills !== undefined ? data.AutoCalculateBills : false));
        this.BerthTransitOffset = jsonableObservable(S.value<number>(data && data.BerthTransitOffset !== undefined ? data.BerthTransitOffset : 0));
        this.BerthType = jsonableObservable(S.value<BerthType>(data && data.BerthType !== undefined ? data.BerthType : 0));
        this.DaylightMooringOnly = jsonableObservable(S.value<boolean>(data && data.DaylightMooringOnly !== undefined ? data.DaylightMooringOnly : false));
        this.Guid = jsonableObservable(S.value<string>(data && data.Guid !== undefined ? data.Guid : '00000000-0000-0000-0000-000000000000'));
        this.Length = data && data.Length !== undefined ? new Measurement(data.Length) : new Measurement();
        this.LengthBuffer = data && data.LengthBuffer !== undefined ? new Measurement(data.LengthBuffer) : new Measurement();
        this.LowFootmarkCompassPoint = jsonableObservable(S.value<CompassPoint>(data && data.LowFootmarkCompassPoint !== undefined ? data.LowFootmarkCompassPoint : 0));
        this.MaxDraftAllowed = data && data.MaxDraftAllowed !== undefined ? new Measurement(data.MaxDraftAllowed) : new Measurement();
        this.MultiVesselBerthing = jsonableObservable(S.value<boolean>(data && data.MultiVesselBerthing !== undefined ? data.MultiVesselBerthing : false));
        this.Notes = jsonableObservable(S.value<string | null>(data && data.Notes !== undefined ? data.Notes : null));
        this.PierMark = data && data.PierMark !== undefined ? new Measurement(data.PierMark) : new Measurement();
        this.PortName = jsonableObservable(S.value<string | null>(data && data.PortName !== undefined ? data.PortName : null));
        this.PortSideVesselHasBowToLowFootmark = jsonableObservable(S.value<boolean>(data && data.PortSideVesselHasBowToLowFootmark !== undefined ? data.PortSideVesselHasBowToLowFootmark : false));
        this.StartMark = data && data.StartMark !== undefined ? new Measurement(data.StartMark) : new Measurement();
        this.TimeBuffer = jsonableObservable(S.value<number>(data && data.TimeBuffer !== undefined ? data.TimeBuffer : 0));
    }

    static key(obj : Berth) {
        return obj.Id();
    }
    
    static dataKey(obj : BerthOptions) {
        return obj.Id;
    }

    static update(obj : Berth, data : BerthOptions) {
        S.freeze(() => {
            if (data.Id !== undefined) obj.Id(data.Id);
            if (data.Url !== undefined) obj.Url(data.Url);
	        
            if (data.AmenitiesUrl !== undefined) obj.AmenitiesUrl(data.AmenitiesUrl);
            if (data.BerthCapabilityAssignmentsUrl !== undefined) obj.BerthCapabilityAssignmentsUrl(data.BerthCapabilityAssignmentsUrl);
            if (data.PierGroupId !== undefined) obj.PierGroupId(data.PierGroupId);
            if (data.PostalAddressId !== undefined) obj.PostalAddressId(data.PostalAddressId);
            if (data.SchedulingAuthorityId !== undefined) obj.SchedulingAuthorityId(data.SchedulingAuthorityId);
	        
            if (data.AutoCalculateBills !== undefined) obj.AutoCalculateBills(data.AutoCalculateBills);
            if (data.BerthTransitOffset !== undefined) obj.BerthTransitOffset(data.BerthTransitOffset);
            if (data.BerthType !== undefined) obj.BerthType(data.BerthType);
            if (data.DaylightMooringOnly !== undefined) obj.DaylightMooringOnly(data.DaylightMooringOnly);
            if (data.Guid !== undefined) obj.Guid(data.Guid);
            if (data.Length !== undefined) Measurement.update(obj.Length, data.Length);
            if (data.LengthBuffer !== undefined) Measurement.update(obj.LengthBuffer, data.LengthBuffer);
            if (data.LowFootmarkCompassPoint !== undefined) obj.LowFootmarkCompassPoint(data.LowFootmarkCompassPoint);
            if (data.MaxDraftAllowed !== undefined) Measurement.update(obj.MaxDraftAllowed, data.MaxDraftAllowed);
            if (data.MultiVesselBerthing !== undefined) obj.MultiVesselBerthing(data.MultiVesselBerthing);
            if (data.Notes !== undefined) obj.Notes(data.Notes);
            if (data.PierMark !== undefined) Measurement.update(obj.PierMark, data.PierMark);
            if (data.PortName !== undefined) obj.PortName(data.PortName);
            if (data.PortSideVesselHasBowToLowFootmark !== undefined) obj.PortSideVesselHasBowToLowFootmark(data.PortSideVesselHasBowToLowFootmark);
            if (data.StartMark !== undefined) Measurement.update(obj.StartMark, data.StartMark);
            if (data.TimeBuffer !== undefined) obj.TimeBuffer(data.TimeBuffer);
        });
    }
}



// WEBPACK FOOTER //
// ./server/models/Berth.ts